import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { type Toast, type ToastType } from "../contexts/ToastContext";
import {
  CheckCircleIcon,
  XCircleIcon,
  InfoIcon,
  XIcon,
} from "./icons/FeatherIcons";

interface ToastProps {
  toast: Toast;
  onRemove: (id: number) => void;
}

const toastIcons: Record<ToastType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  info: InfoIcon,
};

const toastColors: Record<ToastType, string> = {
  success: "bg-green-500",
  error: "bg-red-500",
  info: "bg-blue-500",
};

const ToastComponent: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const Icon = toastIcons[toast.type];
  const bgColor = toastColors[toast.type];

  // This effect ensures that if a toast is re-rendered, its timer doesn't get reset inappropriately.
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(toast.id);
    }, 3000);

    return () => clearTimeout(timer);
  }, [toast.id, onRemove]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95, transition: { duration: 0.15 } }}
      className={`relative flex items-center w-full max-w-sm p-3 text-white ${bgColor} rounded-lg shadow-md overflow-hidden`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <div className="ml-3 text-sm font-medium flex-1">{toast.message}</div>
      <button
        onClick={() => onRemove(toast.id)}
        className="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex h-8 w-8 rounded-lg hover:bg-white/20 transition-colors"
        aria-label="Dismiss"
      >
        <XIcon className="w-5 h-5" />
      </button>
    </motion.div>
  );
};

export default ToastComponent;
